import { useCallback, useEffect, useMemo, useState } from 'react'
import { Alert, Button, Card, Space, Switch, Table, Typography, message } from 'antd'
import { SafetyCertificateOutlined } from '@ant-design/icons'
import { adminApi } from '../../api'
import { PageHeader } from '../../components/ui'
import './QualitySettings.css'

type RuleRow = {
  id: number
  rule_code: string
  name: string
  description: string
  enabled: boolean
  sort_order: number
}

type ConfigSnapshot = {
  enabled_on_index: boolean
  rules: RuleRow[]
}

type SettingsResp = {
  settings: { enabled_on_index: boolean }
  rules: RuleRow[]
}

function snapshotFromResp(data: SettingsResp): ConfigSnapshot {
  return {
    enabled_on_index: !!data.settings?.enabled_on_index,
    rules: (data.rules || []).map((r) => ({ ...r })),
  }
}

function configsEqual(a: ConfigSnapshot, b: ConfigSnapshot) {
  if (a.enabled_on_index !== b.enabled_on_index) return false
  if (a.rules.length !== b.rules.length) return false
  return a.rules.every((r, i) => r.id === b.rules[i]?.id && r.enabled === b.rules[i]?.enabled)
}

export default function DesensitizeSettings() {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [enabledOnIndex, setEnabledOnIndex] = useState(false)
  const [rules, setRules] = useState<RuleRow[]>([])
  const [saved, setSaved] = useState<ConfigSnapshot | null>(null)

  const applySnapshot = useCallback((snap: ConfigSnapshot) => {
    setEnabledOnIndex(snap.enabled_on_index)
    setRules(snap.rules.map((r) => ({ ...r })))
    setSaved(snap)
  }, [])

  const load = useCallback(() => {
    setLoading(true)
    return adminApi
      .get<SettingsResp>('/admin/desensitize/settings')
      .then((r) => applySnapshot(snapshotFromResp(r.data)))
      .catch((e) => message.error(e.message))
      .finally(() => setLoading(false))
  }, [applySnapshot])

  useEffect(() => {
    void load()
  }, [load])

  const current: ConfigSnapshot = useMemo(
    () => ({ enabled_on_index: enabledOnIndex, rules }),
    [enabledOnIndex, rules],
  )
  const dirty = saved ? !configsEqual(current, saved) : false
  const enabledRuleCount = rules.filter((r) => r.enabled).length
  const rulesEditable = enabledOnIndex

  const setRuleEnabled = (id: number, enabled: boolean) => {
    if (!rulesEditable) return
    setRules((prev) => prev.map((r) => (r.id === id ? { ...r, enabled } : r)))
  }

  const reset = () => {
    if (saved) applySnapshot(saved)
  }

  const save = () => {
    if (enabledOnIndex && enabledRuleCount === 0) {
      message.warning('已开启知识库入库脱敏，请至少启用一条脱敏规则')
      return
    }
    setSaving(true)
    adminApi
      .put<SettingsResp>('/admin/desensitize/settings', {
        enabled_on_index: enabledOnIndex,
        rules: rules.map((r) => ({ id: r.id, enabled: r.enabled })),
      })
      .then((r) => {
        message.success('脱敏配置已保存')
        applySnapshot(snapshotFromResp(r.data))
      })
      .catch((e) => message.error(e.message))
      .finally(() => setSaving(false))
  }

  return (
    <div className="quality-settings">
      <PageHeader
        title="文档脱敏"
        description="开启知识库入库脱敏后可配置规则；开关与规则一并保存后生效。"
      />

      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="配置说明"
        description={
          <ul className="quality-settings-intro">
            <li>
              <strong>知识库入库脱敏</strong>：开启后，上传/新建文档在分片后、写入 ES 前按规则脱敏。
            </li>
            <li>
              <strong>脱敏规则</strong>：须先开启入库脱敏方可编辑；对知识库入库、前台工具与批量加工统一生效。
            </li>
            <li>
              <strong>保存</strong>：修改后点击页面底部「保存配置」提交。
            </li>
          </ul>
        }
      />

      <Card
        className="quality-card quality-card--full"
        title={
          <Space>
            <SafetyCertificateOutlined />
            脱敏配置
          </Space>
        }
        loading={loading}
      >
        <div className="desensitize-index-row">
          <Typography.Text strong className="desensitize-index-label">
            知识库入库脱敏
          </Typography.Text>
          <Switch checked={enabledOnIndex} onChange={setEnabledOnIndex} />
          <Typography.Text type="secondary" className="desensitize-index-hint">
            上传/新建文档索引时自动脱敏（分片后写入 ES 前）
          </Typography.Text>
        </div>

        <Typography.Text strong style={{ display: 'block', marginBottom: 4, marginTop: 8 }}>
          脱敏规则
        </Typography.Text>
        <Typography.Text type="secondary" className="quality-card-desc">
          {rulesEditable
            ? '配置要识别的敏感信息类型；前台「文档脱敏」与批量加工使用同一套规则。'
            : '请先开启「知识库入库脱敏」后再编辑规则。'}
        </Typography.Text>

        <Table
          rowKey="id"
          pagination={false}
          dataSource={rules}
          style={{ marginTop: 12 }}
          columns={[
            { title: '规则', dataIndex: 'name', width: 120 },
            { title: '说明', dataIndex: 'description', ellipsis: true },
            {
              title: '启用',
              width: 100,
              render: (_: unknown, row: RuleRow) => (
                <Switch
                  checked={row.enabled}
                  disabled={!rulesEditable}
                  onChange={(v) => setRuleEnabled(row.id, v)}
                />
              ),
            },
          ]}
        />

        <div className="quality-settings-footer quality-settings-footer--plain" style={{ marginTop: 24 }}>
          <Space size="middle" className="quality-settings-footer-inner">
            {dirty ? (
              <Typography.Text type="warning" className="quality-dirty-hint">
                有未保存的更改
              </Typography.Text>
            ) : (
              <Typography.Text type="secondary" className="quality-dirty-hint">
                所有更改需保存后生效
              </Typography.Text>
            )}
            <Space>
              <Button disabled={!dirty || saving} onClick={reset}>
                重置
              </Button>
              <Button type="primary" loading={saving} disabled={!dirty} onClick={save}>
                保存配置
              </Button>
            </Space>
          </Space>
        </div>
      </Card>
    </div>
  )
}
